import styled, { css } from 'styled-components';
import { Link } from 'view/common/analytics/link';
import * as mixins from 'view/styles/mixins';
import * as grid from 'view/styles/grid';
import * as fonts from 'view/styles/fonts';
import * as transitions from 'view/styles/transitions';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { isSearchEnhancementsMilestone1Enabled } from 'feature-flags';

interface LinkContainerProps {
    isSuggestion?: boolean;
}

const TitleH3Css = css`
    ${fonts.regular};
    overflow: hidden;
    text-overflow: ellipsis;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 1;
    word-break: break-word;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    text-transform: uppercase;
    margin: ${token('space.0', '0px')};
    font-weight: normal;
    transition: color ${transitions.speedMs.medium}ms ${transitions.timingFunctions.easeInOut};
    color: ${token('color.text', colors.N800)};
`;

export const Title = styled.h3`
    ${TitleH3Css}
`;

const SnippetParagraphCss = css`
    ${fonts.small};
    color: ${token('color.text', colors.N800)};
    margin-top: ${token('space.100', '8px')};
    padding-right: ${token('space.200', '16px')};
    overflow: hidden;
    text-overflow: ellipsis;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    line-height: 20px;
    word-break: break-word;
    white-space: normal;
`;

export const Snippet = styled.p`
    ${SnippetParagraphCss}
`;

export const LinkContainer = styled(Link)<LinkContainerProps>`
    ${mixins.borderRadius};
    ${(props) => mixins.applyIf(!props.isSuggestion, mixins.raiseUpOnHoverOrFocus())};
    display: flex;
    background-color: ${(props) => (props.isSuggestion ? 'none' : token('elevation.surface.raised', colors.N0))};
    padding-top: ${token('space.200', '16px')};
    padding-right: ${token('space.050', '4px')};
    padding-bottom: ${token('space.200', '16px')};
    padding-left: ${isSearchEnhancementsMilestone1Enabled() ? token('space.150', '12px') : token('space.100', '8px')};
    margin: ${(props) => (props.isSuggestion ? '0px' : `${token('space.100', '8px')} 0 ${token('space.200', '16px')}`)};
    &:hover,
    &:focus {
        background: ${(props) =>
            props.isSuggestion
                ? token('color.background.neutral.subtle.hovered', colors.N30)
                : token('elevation.surface.raised', colors.N0)};
        text-decoration: none;
    }
    &:hover h3,
    &:focus h3 {
        color: ${(props) => token('color.link', props.theme.color)};
    }
    ${(props) => {
        /*
         * We have to overwrite the styles that ReactRenderer from '@atlaskit/renderer'
         * is applying. ReactRenderer is used in the Proforma packages.
         * */
        return css`
        #proforma-form-create-app & {
        h3 {
            ${TitleH3Css}
            color: ${colors.text};
            line-height: ${grid.multiple(2.5).px};
        }
        p {
            ${SnippetParagraphCss}
        }
        &:hover,
        &:focus {
            background: ${
                props.isSuggestion
                    ? token('color.background.discovery.hovered', colors.N30)
                    : token('elevation.surface.raised', colors.N0)
            }
            text-decoration: none;
        }
        &:hover h3,
        &:focus h3 {
            color: ${token('color.link', props.theme.color)};
        }
    }
    `;
    }}
`;

export const ArticleContainer = styled.div<{ snippets?: string }>`
    margin-left: ${isSearchEnhancementsMilestone1Enabled() ? token('space.250', '20px') : token('space.050', '4px')};
    ${(props) =>
        !props.snippets
            ? `
            display: flex;
            align-items: center
            `
            : ''}
`;

export const ServiceDeskTitle = styled.div`
    color: ${token('color.text.subtlest', colors.N100)};
    font-size: ${grid.multiple(1.375).px};
`;
